<template>
  <div class="sp" role="group" :aria-label="t('player.settings.title')">

    <!-- ── Playback ── -->
    <div class="sp__section">
      <h3 class="sp__section-title">{{ t('player.settings.playback') }}</h3>

      <!-- Speed -->
      <div class="sp__field">
        <label for="sp-speed" class="sp__field-label">
          {{ t('player.settings.speed') }}
        </label>
        <select
          id="sp-speed"
          class="sp__select"
          :value="player.playbackSpeed.value"
          @change="onSpeedChange"
        >
          <option
            v-for="speed in player.availableSpeeds"
            :key="speed"
            :value="speed"
          >
            {{ speed === 1 ? t('player.settings.normal') : `${speed}x` }}
          </option>
        </select>
      </div>

      <!-- Loop toggle -->
      <label class="sp__toggle">
        <span class="sp__toggle-info">
          <span class="sp__toggle-label">{{ t('player.settings.loop') }}</span>
          <span class="sp__toggle-description">{{ t('player.settings.loopDescription') }}</span>
        </span>
        <span class="sp__switch" :class="{ 'sp__switch--on': player.isLoop.value }">
          <input
            type="checkbox"
            class="sp__switch-input"
            role="switch"
            :checked="player.isLoop.value"
            @change="player.toggleLoop()"
          />
          <span class="sp__switch-track"></span>
        </span>
      </label>
    </div>

    <!-- ── Video & Audio ── -->
    <div v-if="hasVideoAudioSection" class="sp__section">
      <h3 class="sp__section-title">{{ t('player.settings.videoAudio') }}</h3>

      <!-- Quality (hidden when no adaptive levels are available) -->
      <template v-if="player.availableQualities.value.length > 0">
        <div class="sp__field">
          <label for="sp-quality" class="sp__field-label">
            {{ t('player.settings.quality') }}
          </label>
          <select
            id="sp-quality"
            class="sp__select"
            :value="qualitySelectValue"
            @change="onQualityChange"
          >
            <option value="auto">
              {{ t('player.quality.auto') }}{{ showEcoSuffix ? ` (${t('player.quality.eco')})` : '' }}
            </option>
            <option
              v-for="quality in player.availableQualities.value"
              :key="quality.index"
              :value="quality.index"
            >
              {{ quality.name }}
            </option>
          </select>
        </div>

        <!-- Eco mode toggle — only when some quality is above 720p, so it has something to hold back -->
        <label v-if="player.hasEcoHeadroom.value" class="sp__toggle">
          <span class="sp__toggle-info">
            <span class="sp__toggle-label">{{ t('player.controls.ecoMode') }}</span>
            <span class="sp__toggle-description">{{ t('player.settings.ecoDescription') }}</span>
          </span>
          <span class="sp__switch" :class="{ 'sp__switch--on': player.isEcoMode.value }">
            <input
              type="checkbox"
              class="sp__switch-input"
              role="switch"
              :checked="player.isEcoMode.value"
              @change="player.toggleEcoMode()"
            />
            <span class="sp__switch-track"></span>
          </span>
        </label>
      </template>

      <!-- Audio tracks -->
      <div
        v-if="player.availableAudioTracks.value.length > 1"
        class="sp__field"
      >
        <label for="sp-audio" class="sp__field-label">
          {{ t('player.settings.audioTrack') }}
        </label>
        <select
          id="sp-audio"
          class="sp__select"
          :value="player.currentAudioTrackIndex.value"
          @change="onAudioChange"
        >
          <option
            v-for="track in player.availableAudioTracks.value"
            :key="track.index"
            :value="track.index"
          >
            {{ track.name || track.language }}
          </option>
        </select>
      </div>

      <!-- Subtitles -->
      <div
        v-if="player.availableSubtitleTracks.value.length > 0"
        ref="subtitlesFieldRef"
        class="sp__field"
        :class="{ 'sp__field--highlight': isHighlighted }"
      >
        <label for="sp-subtitles" class="sp__field-label">
          {{ t('player.settings.subtitles') }}
        </label>
        <select
          id="sp-subtitles"
          class="sp__select"
          :value="subtitleSelectValue"
          @change="onSubtitleChange"
        >
          <option value="off">
            {{ t('player.panels.subtitles.off') }}
          </option>
          <option
            v-for="track in player.availableSubtitleTracks.value"
            :key="track.index"
            :value="track.index"
          >
            {{ track.name || track.language }}
          </option>
        </select>
      </div>

      <!-- Subtitle size — only meaningful while subtitles are showing -->
      <template v-if="subtitlesActive">
        <div class="sp__field">
          <label for="sp-subtitle-size" class="sp__field-label">
            {{ t('player.settings.subtitleSize') }}
          </label>
          <select
            id="sp-subtitle-size"
            class="sp__select"
            :value="player.subtitleScale.value"
            @change="onSubtitleSizeChange"
          >
            <option
              v-for="preset in subtitleScalePresets"
              :key="preset.value"
              :value="preset.value"
            >
              {{ t(`player.settings.subtitleSizes.${preset.labelKey}`) }}
            </option>
          </select>
        </div>
      </template>
    </div>

    <!-- ── Accessibility & Help ── -->
    <div class="sp__section">
      <h3 class="sp__section-title">{{ t('player.settings.accessibilityHelp') }}</h3>

      <button
        class="sp__card sp__card--shortcuts"
        @click="player.togglePanel(PanelType.KEYBOARD_SHORTCUTS)"
      >
        <span class="sp__card-icon-wrapper">
          <svg class="sp__card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/>
          </svg>
        </span>
        <span class="sp__card-text">
          <span class="sp__card-label">{{ t('player.settings.shortcuts') }}</span>
          <span class="sp__card-description">{{ t('player.settings.viewShortcuts') }}</span>
        </span>
        <svg class="sp__card-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>

    <!-- ── About ── -->
    <div class="sp__section sp__section--footer">
      <div class="sp__about">
        <span class="sp__about-name">Videas Player</span>
        <span class="sp__about-version">{{ t('player.settings.version') }} 1.0</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { PanelType } from '../../types'
import { useVideoI18n } from '../../i18n'
import { SUBTITLE_SCALE_PRESETS } from '../../../common/composables/usePlayerPreferences'

interface Props {
  player: UseVideoPlayerReturn
}

const props = defineProps<Props>()
const { t } = useVideoI18n()

const subtitlesFieldRef = ref<HTMLElement | null>(null)
const isHighlighted = ref(false)
let highlightTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.player.panelHighlight.value, (highlight) => {
  if (highlight === 'subtitles') {
    nextTick(() => {
      const el = subtitlesFieldRef.value
      if (el) {
        const scrollContainer = el.closest('.info-panel__content')
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect()
          const elRect = el.getBoundingClientRect()
          const offset = elRect.top - containerRect.top - (containerRect.height - elRect.height) / 2
          scrollContainer.scrollBy({ top: offset, behavior: 'smooth' })
        }
      }
      isHighlighted.value = true
      if (highlightTimer) clearTimeout(highlightTimer)
      highlightTimer = setTimeout(() => {
        isHighlighted.value = false
        props.player.panelHighlight.value = null
      }, 3000)
    })
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (highlightTimer) clearTimeout(highlightTimer)
})

// Hide the whole Video & Audio section when nothing in it is available
// (direct-src mode on a plain MP4: no qualities, no extra audio tracks, no subs).
const hasVideoAudioSection = computed(() =>
  props.player.availableQualities.value.length > 0
    || props.player.availableAudioTracks.value.length > 1
    || props.player.availableSubtitleTracks.value.length > 0,
)

// "Auto (Eco)" only where eco mode holds something back: on a stream that stops at 720p it is plain Auto
const showEcoSuffix = computed(() => props.player.isEcoMode.value && props.player.hasEcoHeadroom.value)

// Quality select needs a combined value: "auto" or the numeric index
const qualitySelectValue = computed(() =>
  props.player.isAutoQuality.value ? 'auto' : props.player.currentQualityIndex.value,
)

// Subtitle select: "off" or the numeric track index
const subtitleSelectValue = computed(() =>
  !props.player.subtitlesEnabled.value || props.player.currentSubtitleTrackIndex.value === -1
    ? 'off'
    : props.player.currentSubtitleTrackIndex.value,
)

// Subtitle size control is only relevant when a track is actually showing
const subtitlesActive = computed(() =>
  props.player.availableSubtitleTracks.value.length > 0 && subtitleSelectValue.value !== 'off',
)

const subtitleScalePresets = SUBTITLE_SCALE_PRESETS

function onSpeedChange(e: Event) {
  const value = Number((e.target as HTMLSelectElement).value)
  props.player.setPlaybackSpeed(value)
}

function onQualityChange(e: Event) {
  const raw = (e.target as HTMLSelectElement).value
  props.player.setQualityLevel(raw === 'auto' ? -1 : Number(raw))
}

function onAudioChange(e: Event) {
  const value = Number((e.target as HTMLSelectElement).value)
  props.player.setAudioTrack(value)
}

function onSubtitleChange(e: Event) {
  const raw = (e.target as HTMLSelectElement).value
  if (raw === 'off') {
    props.player.disableSubtitles()
  } else {
    props.player.enableSubtitles(Number(raw))
  }
}

function onSubtitleSizeChange(e: Event) {
  props.player.setSubtitleScale(Number((e.target as HTMLSelectElement).value))
}
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.sp {
  padding: 8px 0;
  overflow-x: hidden;

  @include player-compact {
    padding: 4px 0;

    .sp__section {
      padding: 8px 12px;
    }
  }

  // ── Sections ──
  &__section {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 16px;
    min-width: 0;

    & + & {
      padding-top: 16px;
    }

    &--footer {
      margin-top: auto;

      // The product's name and version are a line of a short frame lost.
      @include player-compact {
        display: none;
      }
    }
  }

  &__section-title {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    // text-faint (0.4 alpha) failed contrast on both themes for this small
    // uppercase header; tertiary (0.7) stays muted but readable (~8:1+).
    color: var(--vp-text-tertiary);
    margin: 0 0 2px 0;
  }

  // ── Select fields (speed, quality, audio) ──
  &__field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 8px;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
      border-color: var(--vp-border-default);
    }

    &--highlight {
      animation: field-highlight 3s ease;
    }
  }

  &__field-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-secondary);
    white-space: nowrap;
  }

  &__select {
    appearance: none;
    padding: 5px 28px 5px 10px;
    min-width: 100px;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: var(--vp-surface-hover);
    color: var(--vp-text-primary);
    border: 1px solid var(--vp-border-default);
    border-radius: 6px;
    font-size: 0.75rem;
    font-family: inherit;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
    color-scheme: var(--vp-color-scheme, dark);

    background-image: var(--vp-select-arrow);
    background-repeat: no-repeat;
    background-position: right 6px center;
    background-size: 14px;

    option {
      background-color: var(--vp-select-option-bg);
      color: var(--vp-text-primary);
    }

    &:hover {
      background-color: var(--vp-surface-active);
      border-color: var(--vp-border-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  // ── Toggle switches (loop, eco, subtitles) ──
  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
      border-color: var(--vp-border-default);
    }
  }

  &__toggle-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__toggle-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-primary);
  }

  &__toggle-description {
    font-size: 0.75rem;
    // text-muted (0.5 alpha) fell under 4.5:1 on the light theme and over a
    // bright frame on the dark one.
    color: var(--vp-text-tertiary);
  }

  &__switch {
    position: relative;
    width: 40px;
    height: 22px;
    flex-shrink: 0;
  }

  &__switch-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__switch-track {
    position: absolute;
    inset: 0;
    background-color: var(--vp-surface-elevated);
    border-radius: 22px;
    transition: background-color 0.2s ease;
    cursor: pointer;

    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background-color: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
    }
  }

  &__switch--on &__switch-track {
    background-color: var(--vp-primary-color, #007bff);

    &::after {
      transform: translateX(18px);
    }
  }

  &__switch-input:focus-visible + &__switch-track {
    outline: 2px solid var(--vp-primary-color, #007bff);
    outline-offset: 2px;
  }

  // ── Card-style action (keyboard shortcuts) ──
  &__card {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-width: 0;
    padding: 10px 12px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 8px;
    color: var(--vp-text-secondary);
    cursor: pointer;
    text-align: left;
    text-decoration: none;
    font-family: inherit;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
      border-color: var(--vp-border-default);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }

    // A phone has no keyboard to use them with. Nested so it comes after
    // `display: flex` at the same specificity.
    &--shortcuts {
      @media (hover: none) and (pointer: coarse) {
        display: none;
      }
    }
  }

  &__card-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    background: var(--vp-border-subtle);
    border-radius: 6px;
  }

  &__card-icon {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }

  &__card-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__card-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-primary);
  }

  &__card-description {
    font-size: 0.75rem;
    // Same as the toggle description.
    color: var(--vp-text-tertiary);
  }

  &__card-arrow {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.3;
    transition: opacity 0.15s ease;

    .sp__card:hover & {
      opacity: 0.6;
    }
  }

  // ── About ──
  &__about {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 0;
    font-size: 0.75rem;
    // `--vp-text-ghost` (0.3 alpha) failed WCAG AA contrast (~2:1) in both
    // themes for this small text. `--vp-text-secondary` (0.85) clears 4.5:1
    // while staying visually subtle in the footer.
    color: var(--vp-text-secondary);
  }

  &__about-name {
    font-weight: 500;
  }

  &__about-version {
    font-size: 0.75rem;
  }
}

@keyframes field-highlight {
  0% {
    background-color: var(--vp-surface-subtle);
    border-color: var(--vp-border-subtle);
  }
  10%, 60% {
    background-color: var(--vp-surface-elevated);
    border-color: var(--vp-border-hover);
  }
  100% {
    background-color: var(--vp-surface-subtle);
    border-color: var(--vp-border-subtle);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sp__switch-track,
  .sp__switch-track::after {
    transition: none;
  }

  .sp__field--highlight {
    animation: none;
    background-color: var(--vp-surface-active);
    border-color: var(--vp-border-hover);
  }
}
</style>
